import { useQuery } from '@tanstack/react-query';
import { messagesAPI } from '../utils/api';
import { MessageLog } from '../types';
import {
  XMarkIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
} from '@heroicons/react/24/outline';

interface MessageDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  messageId: string | null;
}

export default function MessageDetailModal({ isOpen, onClose, messageId }: MessageDetailModalProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['message', messageId],
    queryFn: async () => {
      const response = await messagesAPI.getMessage(messageId!);
      return response.data.data as MessageLog;
    },
    enabled: isOpen && !!messageId,
  });

  if (!isOpen || !messageId) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      dateStyle: 'full',
      timeStyle: 'medium',
    }).format(date);
  };

  const getChannelBadge = (channel: string) => {
    if (channel === 'telegram') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
          Telegram
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <DevicePhoneMobileIcon className="h-3 w-3 mr-1" />
        WhatsApp
      </span>
    );
  };

  const getDirectionBadge = (direction: string) => {
    if (direction === 'inbound') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
          <ArrowDownLeftIcon className="h-3 w-3 mr-1" />
          In entrata
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700">
        <ArrowUpRightIcon className="h-3 w-3 mr-1" />
        In uscita
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      sent: { color: 'bg-gray-100 text-gray-800', label: 'Inviato' },
      delivered: { color: 'bg-blue-100 text-blue-800', label: 'Consegnato' },
      read: { color: 'bg-green-100 text-green-800', label: 'Letto' },
      failed: { color: 'bg-red-100 text-red-800', label: 'Fallito' },
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Dettaglio Messaggio</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : data ? (
            <div className="space-y-4">
              {/* ID */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">ID</label>
                <p className="text-sm text-gray-900 font-mono">{data.id}</p>
              </div>

              {/* Data/Ora */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Data/Ora</label>
                <p className="text-sm text-gray-900 flex items-center gap-1">
                  <ClockIcon className="h-4 w-4 text-gray-400" />
                  {formatDate(data.created_at)}
                </p>
              </div>

              {/* Canale + Direzione + Stato in a row */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Canale</label>
                  <div className="mt-1">{getChannelBadge(data.channel)}</div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Direzione</label>
                  <div className="mt-1">{getDirectionBadge(data.direction)}</div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Stato</label>
                  <div className="mt-1">{getStatusBadge(data.message_status)}</div>
                </div>
              </div>

              {/* Mittente + Destinatario */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Mittente</label>
                  <p className="text-sm text-gray-900 font-mono">{data.sender_id}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Destinatario</label>
                  <p className="text-sm text-gray-900 font-mono">{data.recipient_id}</p>
                </div>
              </div>

              {/* Messaggio */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Messaggio</label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm text-gray-900 whitespace-pre-wrap">
                  {data.message_text || <span className="text-gray-400 italic">Nessun testo</span>}
                </div>
              </div>

              {/* Media */}
              {(data.media_type || data.media_url) && (
                <div className="grid grid-cols-2 gap-4">
                  {data.media_type && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">Tipo Media</label>
                      <p className="text-sm text-gray-900">{data.media_type}</p>
                    </div>
                  )}
                  {data.media_url && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">URL Media</label>
                      <p className="text-sm text-gray-900 break-all">{data.media_url}</p>
                    </div>
                  )}
                </div>
              )}

              {/* External Message ID + Session ID */}
              <div className="grid grid-cols-2 gap-4">
                {data.external_message_id && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">External Message ID</label>
                    <p className="text-sm text-gray-900 font-mono break-all">{data.external_message_id}</p>
                  </div>
                )}
                {data.session_id && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">Session ID</label>
                    <p className="text-sm text-gray-900 font-mono">{data.session_id}</p>
                  </div>
                )}
              </div>

              {/* Metadata JSON */}
              {data.metadata_json && Object.keys(data.metadata_json).length > 0 && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Metadata</label>
                  <pre className="mt-1 p-3 bg-gray-50 rounded-lg text-xs text-gray-900 overflow-x-auto">
                    {JSON.stringify(data.metadata_json, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Messaggio non trovato</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
